import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../components';
import DarkModeToggle from '../components/DarkModeToggle';
import { apiClient } from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { broadcastLogin, waitForAcknowledgement, useAuthListener } from '../lib/useAuthSync';

/**
 * Device Verification Page
 *
 * Shown when user logs in from an unrecognized device.
 * This page:
 * 1. Displays a message to check email
 * 2. Allows resending verification email
 * 3. Verifies device token from email link
 * 4. Redirects to dashboard after verification
 */
export default function DeviceVerificationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [loginDetected, setLoginDetected] = useState(false);
  const { setUser, setWorkspace } = useAuthStore();

  // Get email from state (passed from LoginPage)
  const email = sessionStorage.getItem('deviceVerificationEmail') || '';

  // Listen for login events from other tabs (when device is verified)
  useAuthListener(() => {
    // User verified device in another tab!
    // Show success message briefly, then redirect
    setLoginDetected(true);
    setTimeout(() => {
      const { workspace } = useAuthStore.getState();
      if (workspace && workspace.slug) {
        navigate(`/${workspace.slug}/dashboard`);
      } else {
        navigate('/login');
      }
    }, 1000); // 1 second delay
  });

  useEffect(() => {
    // If no email in session storage, redirect to login
    if (!email && !searchParams.get('token')) {
      navigate('/login');
      return;
    }

    // Handle token from email link
    const verifyToken = async () => {
      const token = searchParams.get('token');
      if (!token) return;

      setIsVerifying(true);
      setError('');

      try {
        const response = await apiClient.get(
          `/auth/device/verify?token=${encodeURIComponent(token)}`
        );

        console.log('[DEVICE] Device verification response:', response.data);

        if (response.data.success) {
          const { user, workspace } = response.data.data;

          console.log('[DEVICE] Setting auth state:', { user, workspace });

          // Update auth store
          setUser(user);
          setWorkspace(workspace);

          // Clear session storage
          sessionStorage.removeItem('deviceVerificationEmail');

          setSuccess(true);

          // Broadcast login to other tabs
          broadcastLogin();

          // Wait for acknowledgement from other tabs (500ms timeout)
          const hasOtherTabs = await waitForAcknowledgement(500);

          console.log('Has other tabs listening:', hasOtherTabs);

          if (!hasOtherTabs) {
            // Single-tab scenario - no other tabs listening
            // Show redirecting state and redirect after 1 second
            console.log('No other tabs detected, will redirect in 1 second');
            setIsRedirecting(true);
            setTimeout(() => {
              console.log('Redirecting...');
              if (workspace.onboardingCompleted) {
                navigate(`/${workspace.slug}/dashboard`);
              } else {
                navigate('/onboarding');
              }
            }, 1000);
          } else {
            console.log('Other tabs detected, letting them handle redirect');
          }
          // If hasOtherTabs = true, the other tab will handle the redirect
          // This tab stays on success screen for user to close
        }
      } catch (err: any) {
        setError(
          err.response?.data?.message || t('auth.deviceVerification.verifyError')
        );
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate, email, setUser, setWorkspace, t]);

  useEffect(() => {
    // Countdown timer for resend button
    if (resendTimer > 0) {
      const timeout = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [resendTimer]);

  const handleResend = async () => {
    if (resendTimer > 0) return;

    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      // Re-initiate login to trigger new device verification email
      await apiClient.post('/auth/login', {
        email,
        password: '', // Backend will reject but still send email if device needs verification
      });
    } catch (err: any) {
      // Expected: login will fail but email should be sent
      if (err.response?.data?.requireDeviceVerification) {
        setResendSuccess(true);
        setResendTimer(60); // 60 second cooldown
      } else {
        setError(t('auth.deviceVerification.resendError'));
      }
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 p-4 transition-colors duration-300">
      <Card className="w-full max-w-md relative">
        <DarkModeToggle />

        <div className="mb-8 text-center">
          <img
            src="/logo.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 dark:hidden"
          />
          <img
            src="/logo-white.png"
            alt={t('app.name')}
            className="h-24 mx-auto mb-6 hidden dark:block"
          />
          <h1 className="text-3xl font-bold mb-2 dark:text-gray-100">
            {t('auth.deviceVerification.title')}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            {t('auth.deviceVerification.subtitle')}
          </p>
        </div>

        {loginDetected ? (
          <div className="space-y-6">
            <div className="p-6 bg-indigo-50 dark:bg-indigo-900/20 border-2 border-indigo-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <h3 className="font-semibold text-indigo-900 dark:text-indigo-200 mb-1">
                    {t('auth.deviceVerification.successTitle')}
                  </h3>
                  <p className="text-sm text-indigo-800 dark:text-indigo-300">
                    {t('auth.deviceVerification.successMessage')}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-indigo-200 dark:border-indigo-700 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin"></div>
            </div>
          </div>
        ) : isVerifying ? (
          <div className="space-y-6">
            <div className="flex items-center justify-center gap-3 p-6 bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-black dark:border-white"></div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                {t('auth.deviceVerification.verifying')}
              </p>
            </div>
          </div>
        ) : success ? (
          <div className="space-y-6">
            <div className="p-6 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
              <div className="flex items-start gap-4">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div className="w-full">
                  <h3 className="font-semibold text-green-900 dark:text-green-200 mb-2">
                    {t('auth.deviceVerification.successTitle')}
                  </h3>
                  <p className="text-sm text-green-800 dark:text-green-300 mb-3">
                    {t('auth.deviceVerification.successMessage')}
                  </p>
                  {isRedirecting ? (
                    <div className="pt-3 border-t border-green-200 dark:border-green-700">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        {t('auth.verify.redirectingNow')}
                      </p>
                    </div>
                  ) : (
                    <div className="pt-3 border-t border-green-200 dark:border-green-700">
                      <p className="text-sm text-green-700 dark:text-green-400 font-medium">
                        {t('auth.verify.canCloseTab')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {isRedirecting && (
              <div className="flex justify-center">
                <div className="w-8 h-8 border-4 border-green-200 dark:border-green-700 border-t-green-600 dark:border-t-green-400 rounded-full animate-spin"></div>
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="space-y-6">
              {/* Email sent message */}
              <div className="p-6 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 rounded-lg">
                <div className="flex items-start gap-4">
                  <svg
                    className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <div>
                    <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-1">
                      {t('auth.deviceVerification.checkEmailTitle')}
                    </h3>
                    <p className="text-sm text-blue-800 dark:text-blue-300 mb-2">
                      {t('auth.deviceVerification.checkEmailMessage', { email })}
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      {t('auth.deviceVerification.checkSpam')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Security info */}
              <div className="p-4 bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg">
                <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                  {t('auth.deviceVerification.whyTitle')}
                </h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {t('auth.deviceVerification.whyMessage')}
                </p>
              </div>

              {/* Resend success message */}
              {resendSuccess && (
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-lg">
                  <p className="text-sm text-green-900 dark:text-green-200">
                    {t('auth.deviceVerification.resendSuccess')}
                  </p>
                </div>
              )}

              {/* Error message */}
              {error && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-lg">
                  <p className="text-sm text-red-900 dark:text-red-200">{error}</p>
                </div>
              )}

              {/* Resend button */}
              <Button
                variant="secondary"
                className="w-full"
                onClick={handleResend}
                isLoading={isResending}
                disabled={resendTimer > 0}
              >
                {resendTimer > 0
                  ? t('auth.deviceVerification.resendTimer', { seconds: resendTimer })
                  : t('auth.deviceVerification.resendButton')}
              </Button>

              {/* Back to login */}
              <div className="pt-6 border-t-2 border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  <a
                    href="/login"
                    className="font-medium text-gray-700 dark:text-gray-300 hover:text-black dark:hover:text-white hover:underline"
                  >
                    {t('auth.deviceVerification.backToLogin')}
                  </a>
                </p>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
